import React, { useState } from 'react';
import { 
    Box, 
    Grid, 
    TextField, 
    Button, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    FormControlLabel, 
    Switch, 
    Typography,
    Tabs,
    Tab,
    InputAdornment,
    Alert,
    Snackbar
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Upload as UploadIcon, Lock as LockIcon } from '@mui/icons-material';
import { ColorPicker } from '../../business/CouponDesigner/ColorPicker';
import { CouponBase, DiscountType } from '../../../types/coupon';
import adminApi from '../../../services/adminApi';

interface TabPanelProps {
    children?: React.ReactNode;
    value: number;
    index: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`branding-tabpanel-${index}`}
            aria-labelledby={`branding-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
        </div>
    );
}

interface CouponFormProps {
    initialData?: Partial<CouponBase>;
    onSubmit: (data: Partial<CouponBase>, goToDesign?: boolean) => void;
    onDesign?: () => void;
}

interface CouponFormErrors {
    code?: string;
    title?: string;
    discountValue?: string;
    marketingSettings?: {
        listId?: string;
        partnerName?: string;
    };
}

const discountOptions = [
    { value: 'percentage', label: 'Percentage off' },
    { value: 'fixed', label: 'Fixed amount off' },
    { value: 'freeShipping', label: 'Free shipping' },
    { value: 'none', label: 'No discount' },
    { value: 'freeform', label: 'Custom discount text' }
];

const defaultMarketingSettings = {
    listId: process.env.REACT_APP_DEFAULT_MARKETING_LIST_ID || '40',
    partnerName: process.env.REACT_APP_DEFAULT_PARTNER_NAME || 'QRPon'
};

const CouponForm: React.FC<CouponFormProps> = ({
    onSubmit,
    onDesign,
    initialData
}) => {
    const [formData, setFormData] = useState<Partial<CouponBase>>({
        ...initialData,
        marketingSettings: initialData?.marketingSettings || {
            listId: '',
            partnerName: ''
        },
        customBranding: initialData?.customBranding || {
            enabled: false,
            company: {},
            theme: {},
            landingPage: {}
        },
        design: initialData?.design || {
            name: 'New Coupon',
            canvas: {
                backgroundColor: '#ffffff',
                breakpoints: {
                    desktop: { width: 800, height: 400 },
                    tablet: { width: 600, height: 300 },
                    mobile: { width: 400, height: 200 }
                }
            },
            elements: [],
            assets: []
        }
    });
    const [errors, setErrors] = useState<CouponFormErrors>({});
    const [brandingTabValue, setBrandingTabValue] = useState(0);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);

    const handleChange = (field: keyof CouponBase, value: any) => {
        // For discount value, ensure proper type conversion
        if (field === 'discountValue') {
            if (formData.discountType === 'percentage' || formData.discountType === 'fixed') {
                value = value === '' ? 0 : Number(value);
            } else if (formData.discountType === 'freeform') {
                value = String(value || '');  
            } else {
                value = null;
            }
        }

        setFormData(prev => ({ ...prev, [field]: value }));
        if (field in errors) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const handleDiscountTypeChange = (type: string) => {
        // Clear discount value when switching types
        let newValue: number | string | null = null;
        
        // Set appropriate initial values based on type
        if (type === 'percentage' || type === 'fixed') {
            newValue = 0;
        } else if (type === 'freeform') {
            newValue = '';  
        } else {
            newValue = null;  
        }
        
        setFormData(prev => ({
            ...prev,
            discountType: type as DiscountType,
            discountValue: newValue
        }));
    };

    const validateForm = (): boolean => {
        const newErrors: CouponFormErrors = {};
        if (!formData.code) newErrors.code = 'Code is required';
        if (!formData.title) newErrors.title = 'Title is required';
        
        // Validate discount value based on type
        if (formData.discountType === 'percentage' || formData.discountType === 'fixed') {
            const numValue = Number(formData.discountValue);
            if (isNaN(numValue) || numValue < 0) {
                newErrors.discountValue = 'Discount value must be a positive number';
            }
        } else if (formData.discountType === 'freeform') {
            const strValue = String(formData.discountValue || '').trim();  
            if (!strValue) {
                newErrors.discountValue = 'Please enter a discount description';
            }
            // Update the trimmed value
            setFormData(prev => ({ ...prev, discountValue: strValue }));
        }
        
        if (!formData.marketingSettings?.listId) {
            newErrors.marketingSettings = { ...newErrors.marketingSettings, listId: 'Marketing list ID is required' };
        }
        if (!formData.marketingSettings?.partnerName) {
            newErrors.marketingSettings = { ...newErrors.marketingSettings, partnerName: 'Partner name is required' };
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent, goToDesign: boolean = false) => {
        console.log('CouponForm handleSubmit called', { goToDesign });
        e.preventDefault();
        const isValid = validateForm();
        console.log('Form validation result:', { isValid, errors });
        
        if (isValid) {
            try {
                console.log('Submitting form data:', formData);
                await onSubmit(formData, goToDesign);
                console.log('Form submission successful');
                if (!goToDesign) {
                    setShowSuccessMessage(true);
                }
            } catch (error) {
                console.error('Error in form submission:', error);
            }
        }
    };

    const handleSaveAndDesign = async (e: React.FormEvent) => {
        console.log('CouponForm handleSaveAndDesign called');
        e.preventDefault();
        const isValid = validateForm();
        console.log('Form validation result (Save & Design):', { isValid, errors });
        
        if (isValid) {
            try {
                console.log('Calling handleSubmit with goToDesign=true');
                await handleSubmit(e, true);
            } catch (error) {
                console.error('Error in save and design:', error);
            }
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box component="form" onSubmit={(e) => handleSubmit(e, false)} sx={{ mt: 3 }}>
                <Snackbar 
                    open={showSuccessMessage} 
                    autoHideDuration={3000} 
                    onClose={() => setShowSuccessMessage(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert severity="success" onClose={() => setShowSuccessMessage(false)}>
                        Coupon saved successfully!
                    </Alert>
                </Snackbar>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Code"
                            value={formData.code || ''}
                            onChange={(e) => handleChange('code', e.target.value)}
                            error={!!errors.code}
                            helperText={errors.code}
                            InputProps={{
                                readOnly: !!initialData?.code,
                                endAdornment: initialData?.code ? (
                                    <InputAdornment position="end">
                                        <LockIcon color="action" fontSize="small" />
                                    </InputAdornment>
                                ) : null
                            }}
                            sx={{
                                '& .MuiInputBase-input.Mui-readOnly': {
                                    bgcolor: 'action.hover',
                                    cursor: 'not-allowed'
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Title"
                            value={formData.title || ''}
                            onChange={(e) => handleChange('title', e.target.value)}
                            error={!!errors.title}
                            helperText={errors.title}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Description"
                            value={formData.description || ''}
                            onChange={(e) => handleChange('description', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Discount Type</InputLabel>
                            <Select
                                value={formData.discountType || 'percentage'}
                                onChange={(e) => handleDiscountTypeChange(e.target.value)}
                                label="Discount Type"
                            >
                                {discountOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {(formData.discountType === 'percentage' || formData.discountType === 'fixed') && (
                            <TextField
                                fullWidth
                                type="number"
                                label={formData.discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount'}
                                value={formData.discountValue ?? ''}
                                onChange={(e) => handleChange('discountValue', e.target.value)}
                                error={!!errors.discountValue}
                                helperText={errors.discountValue}
                                InputProps={{
                                    startAdornment: formData.discountType === 'fixed' ? (
                                        <InputAdornment position="start">$</InputAdornment>
                                    ) : undefined,
                                    endAdornment: formData.discountType === 'percentage' ? (
                                        <InputAdornment position="end">%</InputAdornment>
                                    ) : undefined,
                                    inputProps: { min: 0 }
                                }}
                            />
                        )}
                        {formData.discountType === 'freeform' && (
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Discount Description"
                                value={formData.discountValue || ''}
                                onChange={(e) => handleChange('discountValue', e.target.value)}
                                error={!!errors.discountValue}
                                helperText={errors.discountValue || 'Enter custom discount text (e.g., "Buy one get one free")'}
                                placeholder="Enter your custom discount text here"
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <DateTimePicker
                            label="Signup Start Date"
                            value={formData.startDate ? new Date(formData.startDate) : null}
                            onChange={(date) => handleChange('startDate', date?.toISOString())}
                            sx={{ width: '100%' }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <DateTimePicker
                            label="Signup End Date"
                            value={formData.endDate ? new Date(formData.endDate) : null}
                            onChange={(date) => handleChange('endDate', date?.toISOString())}
                            sx={{ width: '100%' }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <DateTimePicker
                            label="Valid From (Redemption Start)"
                            value={formData.validFrom ? new Date(formData.validFrom) : null}
                            onChange={(date) => handleChange('validFrom', date?.toISOString())}
                            sx={{ width: '100%' }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <DateTimePicker
                            label="Valid Until (Redemption End)"
                            value={formData.validUntil ? new Date(formData.validUntil) : null}
                            onChange={(date) => handleChange('validUntil', date?.toISOString())}
                            sx={{ width: '100%' }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Minimum Purchase Amount"
                            value={formData.minPurchaseAmount || ''}
                            onChange={(e) => handleChange('minPurchaseAmount', Number(e.target.value))}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Maximum Redemptions"
                            value={formData.maxRedemptions || ''}
                            onChange={(e) => handleChange('maxRedemptions', Number(e.target.value))}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            Marketing Settings
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Marketing List ID"
                                    value={formData.marketingSettings?.listId || ''}
                                    onChange={(e) => handleChange('marketingSettings', {
                                        ...formData.marketingSettings,
                                        listId: e.target.value
                                    })}
                                    error={!!errors.marketingSettings?.listId}
                                    helperText={errors.marketingSettings?.listId}
                                    placeholder={defaultMarketingSettings.listId}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Partner Name"
                                    value={formData.marketingSettings?.partnerName || ''}
                                    onChange={(e) => handleChange('marketingSettings', {
                                        ...formData.marketingSettings,
                                        partnerName: e.target.value
                                    })}
                                    error={!!errors.marketingSettings?.partnerName}
                                    helperText={errors.marketingSettings?.partnerName}
                                    placeholder={defaultMarketingSettings.partnerName}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Branding Overrides Section - Only shown if feature flag is enabled */}
                    {process.env.REACT_APP_ENABLE_BRANDING_OVERRIDES === 'true' && (
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.customBranding?.enabled || false}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                handleChange('customBranding', {
                                                    enabled: true,
                                                    company: {},
                                                    theme: {},
                                                    landingPage: {}
                                                });
                                            } else {
                                                handleChange('customBranding', {
                                                    enabled: false
                                                });
                                            }
                                        }}
                                    />
                                }
                                label="Enable Custom Branding"
                            />

                            {formData.customBranding?.enabled && (
                                <Box sx={{ width: '100%', mt: 2 }}>
                                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                        <Tabs 
                                            value={brandingTabValue} 
                                            onChange={(_, newValue) => setBrandingTabValue(newValue)}
                                            aria-label="branding settings tabs"
                                        >
                                            <Tab label="Company Info" />
                                            <Tab label="Theme" />
                                            <Tab label="Landing Page" />
                                        </Tabs>
                                    </Box>

                                    <TabPanel value={brandingTabValue} index={0}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Box sx={{ mb: 2 }}>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        id="logo-upload"
                                                        onChange={async (e) => {
                                                            const file = e.target.files?.[0];
                                                            if (!file) return;

                                                            const formDataForUpload = new FormData();
                                                            formDataForUpload.append('logo', file);

                                                            try {
                                                                const response = await adminApi.uploadCouponLogo(formDataForUpload);
                                                                if (response.data.status === 'success') {
                                                                    const newCustomBranding = {
                                                                        ...formData.customBranding,
                                                                        enabled: true,
                                                                        company: {
                                                                            ...formData.customBranding?.company,
                                                                            logo: response.data.data
                                                                        }
                                                                    };
                                                                    handleChange('customBranding', newCustomBranding);
                                                                }
                                                            } catch (error) {
                                                                console.error('Error uploading logo:', error);
                                                            }
                                                        }}
                                                    />
                                                    <label htmlFor="logo-upload">
                                                        <Button
                                                            variant="outlined"
                                                            component="span"
                                                            startIcon={<UploadIcon />}
                                                        >
                                                            Upload Logo
                                                        </Button>
                                                    </label>
                                                    {formData.customBranding?.company?.logo?.url && (
                                                        <Box sx={{ mt: 2 }}>
                                                            <img 
                                                                src={formData.customBranding.company.logo.url} 
                                                                alt="Company Logo" 
                                                                style={{ maxWidth: 200, maxHeight: 100 }}
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Company Name"
                                                    value={formData.customBranding?.company?.name || ''}
                                                    onChange={(e) => handleChange('customBranding', {
                                                        ...formData.customBranding,
                                                        company: {
                                                            ...formData.customBranding?.company,
                                                            name: e.target.value
                                                        }
                                                    })}
                                                    placeholder="Override company name"
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Website URL"
                                                    value={formData.customBranding?.company?.websiteUrl || ''}
                                                    onChange={(e) => handleChange('customBranding', {
                                                        ...formData.customBranding,
                                                        company: {
                                                            ...formData.customBranding?.company,
                                                            websiteUrl: e.target.value
                                                        }
                                                    })}
                                                    placeholder="Override website URL"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2" gutterBottom>Social Links</Typography>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} md={4}>
                                                        <TextField
                                                            fullWidth
                                                            label="Facebook"
                                                            value={formData.customBranding?.company?.socialLinks?.facebook || ''}
                                                            onChange={(e) => handleChange('customBranding', {
                                                                ...formData.customBranding,
                                                                company: {
                                                                    ...formData.customBranding?.company,
                                                                    socialLinks: {
                                                                        ...formData.customBranding?.company?.socialLinks,
                                                                        facebook: e.target.value
                                                                    }
                                                                }
                                                            })}
                                                            placeholder="Facebook URL"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <TextField
                                                            fullWidth
                                                            label="Instagram"
                                                            value={formData.customBranding?.company?.socialLinks?.instagram || ''}
                                                            onChange={(e) => handleChange('customBranding', {
                                                                ...formData.customBranding,
                                                                company: {
                                                                    ...formData.customBranding?.company,
                                                                    socialLinks: {
                                                                        ...formData.customBranding?.company?.socialLinks,
                                                                        instagram: e.target.value
                                                                    }
                                                                }
                                                            })}
                                                            placeholder="Instagram URL"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <TextField
                                                            fullWidth
                                                            label="Twitter"
                                                            value={formData.customBranding?.company?.socialLinks?.twitter || ''}
                                                            onChange={(e) => handleChange('customBranding', {
                                                                ...formData.customBranding,
                                                                company: {
                                                                    ...formData.customBranding?.company,
                                                                    socialLinks: {
                                                                        ...formData.customBranding?.company?.socialLinks,
                                                                        twitter: e.target.value
                                                                    }
                                                                }
                                                            })}
                                                            placeholder="Twitter URL"
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </TabPanel>

                                    <TabPanel value={brandingTabValue} index={1}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <ColorPicker
                                                    label="Primary Color"
                                                    value={formData.customBranding?.theme?.primaryColor || '#1976d2'}
                                                    onChange={(color) => handleChange('customBranding', {
                                                        ...formData.customBranding,
                                                        theme: {
                                                            ...formData.customBranding?.theme,
                                                            primaryColor: color
                                                        }
                                                    })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <ColorPicker
                                                    label="Secondary Color"
                                                    value={formData.customBranding?.theme?.secondaryColor || '#dc004e'}
                                                    onChange={(color) => handleChange('customBranding', {
                                                        ...formData.customBranding,
                                                        theme: {
                                                            ...formData.customBranding?.theme,
                                                            secondaryColor: color
                                                        }
                                                    })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <ColorPicker
                                                    label="Background Color"
                                                    value={formData.customBranding?.theme?.backgroundColor || '#ffffff'}
                                                    onChange={(color) => handleChange('customBranding', {
                                                        ...formData.customBranding,
                                                        theme: {
                                                            ...formData.customBranding?.theme,
                                                            backgroundColor: color
                                                        }
                                                    })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Font Family</InputLabel>
                                                    <Select
                                                        value={formData.customBranding?.theme?.fontFamily || 'Roboto'}
                                                        onChange={(e) => handleChange('customBranding', {
                                                            ...formData.customBranding,
                                                            theme: {
                                                                ...formData.customBranding?.theme,
                                                                fontFamily: e.target.value
                                                            }
                                                        })}
                                                        label="Font Family"
                                                    >
                                                        {['Roboto', 'Open Sans', 'Montserrat', 'Lato'].map((font) => (
                                                            <MenuItem key={font} value={font}>{font}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </TabPanel>

                                    <TabPanel value={brandingTabValue} index={2}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Welcome Heading"
                                                    value={formData.customBranding?.landingPage?.welcomeHeading || ''}
                                                    onChange={(e) => handleChange('customBranding', {
                                                        ...formData.customBranding,
                                                        landingPage: {
                                                            ...formData.customBranding?.landingPage,
                                                            welcomeHeading: e.target.value
                                                        }
                                                    })}
                                                    placeholder="Override default welcome heading"
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Welcome Message"
                                                    value={formData.customBranding?.landingPage?.welcomeMessage || ''}
                                                    onChange={(e) => handleChange('customBranding', {
                                                        ...formData.customBranding,
                                                        landingPage: {
                                                            ...formData.customBranding?.landingPage,
                                                            welcomeMessage: e.target.value
                                                        }
                                                    })}
                                                    placeholder="Override default welcome message"
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Button Text"
                                                    value={formData.customBranding?.landingPage?.buttonText || ''}
                                                    onChange={(e) => handleChange('customBranding', {
                                                        ...formData.customBranding,
                                                        landingPage: {
                                                            ...formData.customBranding?.landingPage,
                                                            buttonText: e.target.value
                                                        }
                                                    })}
                                                    placeholder="Override default button text"
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Footer Text"
                                                    value={formData.customBranding?.landingPage?.footerText || ''}
                                                    onChange={(e) => handleChange('customBranding', {
                                                        ...formData.customBranding,
                                                        landingPage: {
                                                            ...formData.customBranding?.landingPage,
                                                            footerText: e.target.value
                                                        }
                                                    })}
                                                    placeholder="Override default footer text"
                                                />
                                            </Grid>
                                        </Grid>
                                    </TabPanel>
                                </Box>
                            )}
                        </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isActive || false}
                                    onChange={(e) => handleChange('isActive', e.target.checked)}
                                />
                            }
                            label="Active"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.status === 'draft'}
                                    onChange={(e) => handleChange('status', e.target.checked ? 'draft' : 'active')}
                                />
                            }
                            label="Draft"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            {initialData && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={(e) => handleSubmit(e, false)}
                                >
                                    Save
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                color={initialData ? "secondary" : "primary"}
                                onClick={handleSaveAndDesign}
                            >
                                Save & Design
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </LocalizationProvider>
    );
};

export default CouponForm;
